import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash/debounce";
import { useUrlState } from "../../utils/react-custom-hooks/urlStateHandler";
import { JobsPaginator } from "./JobsPaginator";
import { useSelector } from "react-redux";
import {
  getCustomerJobs,
  getCustomerJobFilters,
  refreshJobsStatus,
} from "../../api/jobsApi";
import { RootState } from "../../utils/store";
import { Job, OurJobStatus, UNREADY_STATES } from "./types";
import { JobsTable } from "./JobsTable";
import { Dropdown } from "../../components/tailwind";
import { actionDisplayName } from "../../ui/BulkActionsDropdown";
import { jobStatusDropdownItems } from "./utils";

// TEST PARAMS
const PAGINATOR_SIZE = 10;

export const Jobs: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const {
    urlState: { page, status, q, user, action },
    setParam,
    removeParam,
    settled,
  } = useUrlState<{
    page: number;
    status: OurJobStatus;
    q: string;
    user: string;
    action: string;
  }>({
    page: 1,
  });

  const [fetchingJobs, setFetchingJobs] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  const pendingJobIds = useMemo(() => {
    return jobs
      .filter((job) => UNREADY_STATES.includes(job.status_from_tasks_result))
      .map((job) => job.id);
  }, [jobs]);

  const mounted = useRef(false);

  const getJobs = (
    page: number,
    q: string,
    status: OurJobStatus,
    user: string,
    action: string
  ): void => {
    setFetchingJobs(true);
    getCustomerJobs(token, page, q, status, user, action)
      .then((res) => {
        if (res.current_page === page && mounted.current) {
          setTotalPages(res.total_pages);
          setJobs(res.jobs);
        }
      })
      .finally(() => {
        setFetchingJobs(false);
      })
      .catch(() => {
        handlePageChange(1);
      });
  };

  const debouncedGetJobs = useCallback(debounce(getJobs, 1000), []);

  useEffect(() => {
    if (!settled || !!jobs.length) return;
    getJobs(page, q, status, user, action);
  }, [settled, token, page, user, action]);

  useEffect(() => {
    getCustomerJobFilters(token)
      .then((filters) => {
        setUsers(filters.users);
        setActions(filters.actions);
      })
      .catch((error) => {
        console.error("Error fetching filters:", error);
        setUsers([]);
        setActions([]);
      });
  }, [token]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handlePageChange = (page: number): void => {
    setParam("page", page);
    setFetchingJobs(true);
    debouncedGetJobs(page, q, status, user, action);
  };

  const updatePendingJobsStatus = async (): Promise<void> => {
    const updatedJobs = await refreshJobsStatus(token, pendingJobIds);
    setJobs((prevJobs) =>
      prevJobs.map((job) => ({
        ...job,
        status_from_tasks_result:
          updatedJobs?.[job.id] || job.status_from_tasks_result,
      }))
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (pendingJobIds.length > 0) {
        updatePendingJobsStatus();
      }
    }, 1000); // Pull new statuses every 1 seconds

    return () => clearInterval(interval as any);
  }, [pendingJobIds]);

  return (
    <>
      <h1>Jobs</h1>
      <form className="txu-form txu-logs-filter-form tw-grid-rows-2">
        <div className="tw-col-span-3 tw-relative">
          <input
            type="text"
            className="txu-input tw-w-full"
            placeholder="Search Job by ID"
            value={q || ""}
            onChange={({ target }) => {
              if (!target.value) {
                removeParam("q");
              } else {
                setParam("q", target.value);
              }
              setParam("page", 1);
              debouncedGetJobs(
                1,
                target.value || undefined,
                status,
                user,
                action
              );
            }}
          />
          {q && (
            <i
              className="material-icons tw-absolute tw-right-2 tw-top-[50%] -tw-translate-y-1/2 tw-rounded tw-text-lg tw-leading-[1] tw-text-gray-500 tw-cursor-pointer hover:tw-ring-1 hover:tw-ring-inset hover:tw-ring-gray-500"
              onClick={() => {
                removeParam("q");
                setParam("page", 1);
                debouncedGetJobs(1, undefined, status, user, action);
              }}
            >
              close
            </i>
          )}
        </div>
        <div className="tw-col-span-1">
          <Dropdown<string>
            clearable
            options={users.map((email) => ({
              key: email,
              value: email,
              text: email,
            }))}
            onChange={(value) => {
              if (!value) {
                removeParam("user");
              } else {
                setParam("user", value as string);
              }
              setParam("page", 1);
              debouncedGetJobs(
                1,
                q,
                status,
                (value as string) || undefined,
                action
              );
            }}
            value={user}
            placeholder="User"
          />
        </div>
        <div>
          <Dropdown<string>
            clearable
            options={actions.map((actionName) => ({
              key: actionName,
              value: actionName,
              text: actionDisplayName(actionName),
            }))}
            onChange={(value) => {
              if (!value) {
                removeParam("action");
              } else {
                setParam("action", value as string);
              }
              setParam("page", 1);
              debouncedGetJobs(
                1,
                q,
                status,
                user,
                (value as string) || undefined
              );
            }}
            value={action}
            placeholder="Action"
          />
        </div>
        <div>
          <Dropdown<OurJobStatus>
            clearable
            options={jobStatusDropdownItems}
            onChange={(value) => {
              if (!value) {
                removeParam("status");
              } else {
                setParam("status", value as OurJobStatus);
              }
              setParam("page", 1);
              debouncedGetJobs(
                1,
                q,
                (value as OurJobStatus) || undefined,
                user,
                action
              );
            }}
            value={status}
            placeholder="Status"
          />
        </div>
      </form>
      <JobsPaginator
        currentPage={page}
        totalPages={totalPages}
        paginatorSize={PAGINATOR_SIZE}
        handlePageChange={handlePageChange}
      />
      <JobsTable
        fetchingJobs={fetchingJobs}
        jobs={jobs}
        pendingJobIds={pendingJobIds}
      />
      <JobsPaginator
        currentPage={page}
        totalPages={totalPages}
        paginatorSize={PAGINATOR_SIZE}
        handlePageChange={handlePageChange}
      />
    </>
  );
};
